// docs/131 CL5: the collected pieces the player has looked at on the collection screen, sent to
// the module in batches (`see_collected`), not one call per hover. A piece seen here loses its New
// tag at once; the server's `seen` follows when the batch lands.
import { create } from 'zustand';
import { rpc } from '../net/reducers';

/** How long a batch gathers before it is sent. */
export const SEE_FLUSH_MS = 1500;
/** The most pieces one call carries (fk-world `collection::SEE_BATCH_MAX`). */
export const SEE_BATCH_MAX = 512;

export interface CollectionStore {
  /** Seen on this screen, sent or still waiting. */
  seen: ReadonlySet<number>;
  /** Waiting to be sent. */
  pending: ReadonlySet<number>;
  see(def: number): void;
  flush(): void;
}

let timer: ReturnType<typeof setTimeout> | undefined;

export const useCollection = create<CollectionStore>()((set, get) => ({
  seen: new Set(),
  pending: new Set(),
  see(def) {
    if (get().seen.has(def)) return;
    set({ seen: new Set(get().seen).add(def), pending: new Set(get().pending).add(def) });
    timer ??= setTimeout(() => get().flush(), SEE_FLUSH_MS);
  },
  flush() {
    if (timer) { clearTimeout(timer); timer = undefined; }
    const defs = [...get().pending];
    if (!defs.length) return;
    set({ pending: new Set() });
    for (let i = 0; i < defs.length; i += SEE_BATCH_MAX) {
      const batch = defs.slice(i, i + SEE_BATCH_MAX);
      // a batch that fails goes back in the queue for the next flush
      void rpc.seeCollected(batch).then((r) => {
        if (r.ok) return;
        set({ pending: new Set([...get().pending, ...batch]) });
      });
    }
  },
}));

/** Whether a collected piece still wears its New tag: the server has not marked it seen, and it was not seen here. */
export const isNew = (row: { def: number; seen: boolean }, seenHere: ReadonlySet<number>): boolean => !row.seen && !seenHere.has(row.def);
